import { formatDateString, isValidDateString } from "@via/utils/helpers";
import { DataSourceProvider } from "common";

export const transformPermissions = permissions => {
  return permissions.map(perm => {
    switch (perm.type) {
      case "everyone":
        return { type: perm.type, read: perm.read, write: perm.write };
      case "user":
        return {
          type: perm.type,
          email: perm.email,
          read: perm.read,
          write: perm.write,
        };
      case "group":
        return {
          type: perm.type,
          groupId: perm.groupId,
          read: perm.read,
          write: perm.write,
        };
      default:
        return perm;
    }
  });
};

export const filterPermissions = permissions => {
  return permissions
    .filter(perm => perm.type && (perm.read || perm.write))
    .map(perm => {
      if (perm.type === "everyone") {
        return {
          ...perm,
          write: false,
        };
      }
      return perm;
    });
};

export const deduplicatePermissions = (permissions: any[]) => {
  const uniquePerms = new Map();
  permissions.forEach(perm => {
    const key = `${perm.type}-${perm.email || perm.groupId}`;
    if (!uniquePerms.has(key)) {
      uniquePerms.set(key, perm);
    } else {
      const existingPerm = uniquePerms.get(key);
      existingPerm.read = existingPerm.read || perm.read;
      existingPerm.write = existingPerm.write || perm.write;
    }
  });
  return Array.from(uniquePerms.values());
};

export const renderCell = (info: any, field) => {
  if (field.type === "mongoose.Schema.ObjectId") {
    return info.getValue();
  } else if (field.type === "Array") {
    const value = info.getValue();
    if (value === null || value === undefined) {
      return "";
    }
    const stringify = JSON.stringify(value);
    // if (stringify.length > 100) {
    //   return `${stringify.slice(0, 100)}...`;
    // }
    return stringify || "";
  } else if (field.type === "Date") {
    const value = info.getValue();
    if (value === null || value === undefined) {
      return "";
    }
    return formatDateString(value, "LL");
  } else if (field.type === "Mixed") {
    const value = info.getValue();
    if (value === null || value === undefined) {
      return "";
    }
    return JSON.stringify(value);
  } else if (field.type === "Boolean") {
    return info.getValue()?.toString();
  } else {
    return info.getValue();
  }
};

export type { FilterItem } from "@via/components/advanced-filter/helpers/types";

export const HIDE_COLUMNS = [
  "id",
  "file3",
  "file4",
  "amazon_cre_id",
  "gs_archive_dir",
  "archive_dir",
  "google_cre_id",
  "s3_archive_dir",
  "provider_ids",
  "file_layout",
  "canvas_label",
  "data_source_name",
];

// Spreadsheet column defaults (UI labels, not internal field names)
export const DEFAULT_HIDDEN_SPREADSHEET_COLUMNS = [
  "File3",
  "File4",
  "Google Storage Archive Directory",
  "S3 Archive Directory",
  "Archive Directory",
  "Provider IDs",
  "File Layout",
  "Pairing Type",
];

export function getAllVisibleColumnVisibility(
  labels: string[]
): Record<string, boolean> {
  return labels.reduce(
    (acc, label) => {
      acc[label] = true;
      return acc;
    },
    {} as Record<string, boolean>
  );
}

export function applyDefaultHiddenColumns(
  visibility: Record<string, boolean>,
  labels: string[],
  hiddenColumns: string[] = DEFAULT_HIDDEN_SPREADSHEET_COLUMNS
): Record<string, boolean> {
  if (!labels?.length) {
    return visibility;
  }

  const normalizedLabelMap = labels.reduce<Record<string, string>>(
    (acc, label) => {
      if (typeof label === "string" && label.length > 0) {
        acc[label.toLowerCase()] = label;
      }
      return acc;
    },
    {}
  );

  const updatedVisibility = { ...visibility };

  hiddenColumns.forEach(columnName => {
    const matchingLabel = normalizedLabelMap[columnName.toLowerCase()] ?? null;
    if (matchingLabel) {
      updatedVisibility[matchingLabel] = false;
    }
  });

  return updatedVisibility;
}

export const processError = (err: any, index: number, rowErrors) => {
  if (
    err?.response?.data.errors &&
    Array.isArray(err.response.data.errors) &&
    err.response.data.errors.length > 0
  ) {
    const errorMessages = err.response.data.errors.map(errorDetail => {
      return `${index + 1}. row, ${errorDetail.field ?? "unknown field"} column: ${errorDetail.message ?? "unknown error"}`;
    });
    rowErrors.push(...errorMessages);
  } else if (err?.response?.data?.message) {
    rowErrors.push(
      `Error processing file ${index + 1}: ${err?.response?.data?.message}`
    );
  } else {
    rowErrors.push(
      `Error processing file ${index + 1}: ${err?.message ?? "unknown error"}`
    );
  }
};

export const transformEmptyStringsToNull = (payload: Record<string, any>) => {
  return Object.fromEntries(
    Object.entries(payload).map(([key, value]) => {
      if (typeof value === "string" && value.trim() === "") return [key, null];
      return [key, value];
    })
  );
};

// Map UI operator values to MongoDB operator values
export const mapOperatorToMongo = (uiOperator: string): string => {
  const operatorMap: Record<string, string> = {
    eq: "eq", // Equals
    ne: "not", // Not Equals
    contains: "regex", // Contains
    gt: "gt", // Greater Than
    lt: "lt", // Less Than
    gte: "gte", // Greater Than or Equal
    lte: "lte", // Less Than or Equal
  };
  return operatorMap[uiOperator] || "eq";
};

// Column comparison utility function
export const compareColumnNames = (
  uploadedColumns: string[],
  canvasColumns: string[]
) => {
  if (
    !uploadedColumns ||
    !canvasColumns ||
    uploadedColumns.length === 0 ||
    canvasColumns.length === 0
  ) {
    return {
      additionalColumns: [],
      hasAdditionalColumns: false,
    };
  }

  const canvasLowerSet = new Set(
    canvasColumns
      .filter(col => col && typeof col === "string")
      .map(col => col.toLowerCase().trim())
  );

  const additionalColumns = uploadedColumns.filter(
    col =>
      typeof col === "string" &&
      col.trim() !== "" &&
      !canvasLowerSet.has(col.toLowerCase().trim())
  );

  return {
    additionalColumns,
    hasAdditionalColumns: additionalColumns.length > 0,
  };
};

// Field form data interface for additional fields
export interface FieldFormData {
  name: string;
  label: string;
  type: string; // This will be cast to VmetaFieldType when creating the field
  unique: boolean;
  required: boolean;
  originalColumnName?: string; // Track the original column name from uploaded file
}

/**
 * Converts any value to a string format suitable for spreadsheet display.
 *
 * @param value - The value to convert
 * @returns Object with 'value' property containing the formatted string
 *
 * Rules:
 * - string: returns as-is
 * - null/undefined: returns empty string
 * - number: converts to string
 * - boolean: converts to "true" or "false"
 * - array/object: uses JSON.stringify
 */
export function formatSpreadsheetCellValue(value: any): { value: string } {
  if (value === null || value === undefined) {
    return { value: "" };
  }
  // check if it is a date
  if (typeof value === "string") {
    if (isValidDateString(value)) {
      return { value: formatDateString(value, "YYYY-MM-DD") };
    } else {
      return { value: value };
    }
  }

  if (typeof value === "number") {
    return { value: value.toString() };
  }

  if (typeof value === "boolean") {
    return { value: value.toString() };
  }

  if (typeof value === "object") {
    return { value: JSON.stringify(value) };
  }

  // Fallback for any other type
  return { value: String(value) };
}

/**
 * Analyzes spreadsheet data to determine which columns should be initially visible.
 * Columns that have no values (all empty, null, or undefined) across all rows will be hidden.
 *
 * @param columnLabels - Array of column labels/headers
 * @param data - Spreadsheet data as array of rows, where each row is an array of cells
 * @returns Record mapping column labels to visibility (true = visible, false = hidden)
 *
 * @example
 * const labels = ['name', 'age', 'city'];
 * const data = [
 *   [{ value: 'John' }, { value: '25' }, { value: '' }],
 *   [{ value: 'Jane' }, { value: '30' }, { value: '' }],
 * ];
 * const visibility = getInitialColumnVisibility(labels, data);
 * // Returns: { name: true, age: true, city: false }
 */
export function getInitialColumnVisibility(
  columnLabels: string[],
  data: Array<Array<{ value: string | null }>>
): Record<string, boolean> {
  const visibility: Record<string, boolean> = {};

  columnLabels.forEach((label, colIndex) => {
    // Check if all cells in this column are empty
    const hasValue = data.some(row => {
      const cell = row[colIndex];
      const value = cell?.value;
      return value !== null && value !== undefined && value !== "";
    });

    // Hide column if all values are empty, otherwise show it
    visibility[label] = hasValue;
  });

  return visibility;
}

// ============================================================================
// Cloud Storage Path Utilities
// ============================================================================

/**
 * Parses a credential ID value and returns a normalized number.
 * Positive numbers represent saved credentials, -1 represents IAM role authentication.
 *
 * @param value - The credential ID value to parse (can be string, number, null, or undefined)
 * @returns The parsed credential ID as a number, or -1 for IAM role/invalid values
 *
 * @example
 * parseCredentialId(5);        // Returns: 5
 * parseCredentialId("10");     // Returns: 10
 * parseCredentialId(null);     // Returns: -1 (IAM role)
 * parseCredentialId("");       // Returns: -1 (IAM role)
 * parseCredentialId(-3);       // Returns: -1 (invalid)
 */
export const parseCredentialId = (value: unknown): number => {
  if (value === null || value === undefined || value === "") return -1;
  const num = Number(value);
  return isNaN(num) || num <= 0 ? -1 : num;
};

/**
 * Checks if a file path contains wildcard characters.
 *
 * @param path - The file path to check
 * @returns True if the path contains wildcards (*), false otherwise
 *
 * @example
 * hasWildcard("gs://bucket/folder/*.txt");  // Returns: true
 * hasWildcard("s3://bucket/file.txt");      // Returns: false
 */
export const hasWildcard = (path: string): boolean => {
  return path.includes("*");
};

/**
 * Checks if a path has a file extension.
 *
 * @param path - The path to check
 * @returns True if the last segment contains a file extension
 *
 * @example
 * hasFileExtension("file.txt");           // Returns: true
 * hasFileExtension("data.tar.gz");        // Returns: true
 * hasFileExtension("folder/README");      // Returns: false
 * hasFileExtension("folder/subfolder");   // Returns: false
 */
export const hasFileExtension = (path: string): boolean => {
  const lastSegment = path.split("/").pop() || "";
  // Check for common file extension pattern (dot followed by alphanumeric chars at end)
  return /\.[a-zA-Z0-9]+$/.test(lastSegment);
};

/**
 * Determines if a cloud storage path represents a file, directory, or is uncertain.
 *
 * Decision logic:
 * 1. Ends with "/" → definitely a directory (false)
 * 2. Has file extension → definitely a file (true)
 * 3. No slash, no extension → uncertain ("uncertain") - needs API validation
 *
 * The "uncertain" case handles ambiguous paths like "bucket/folder" which could
 * be a directory named "folder" or a file named "folder" without extension.
 * For these cases, the caller should try listing as directory first, and if
 * empty, treat as a file.
 *
 * @param path - The cloud storage path to check
 * @returns true (file), false (directory), or "uncertain" (needs validation)
 *
 * @example
 * looksLikeFile("s3://bucket/folder/file.txt");     // Returns: true (has extension)
 * looksLikeFile("s3://bucket/folder/README");       // Returns: "uncertain" (no extension)
 * looksLikeFile("gs://bucket/data.csv");            // Returns: true (has extension)
 * looksLikeFile("s3://bucket/folder/");             // Returns: false (trailing slash)
 * looksLikeFile("gs://bucket/folder");              // Returns: "uncertain" (no extension)
 */
export const looksLikeFile = (path: string): boolean | "uncertain" => {
  // If ends with /, it's definitely a directory
  if (path.endsWith("/")) return false;

  // If has a file extension, it's definitely a file
  if (hasFileExtension(path)) return true;

  // No trailing slash and no extension - could be either
  // Examples: "bucket/folder" (directory) or "bucket/README" (file)
  return "uncertain";
};

export const CLOUD_PROTOCOL = {
  S3: "s3",
  GS: "gs",
} as const;

export type CloudProtocol =
  (typeof CLOUD_PROTOCOL)[keyof typeof CLOUD_PROTOCOL];

export const CLOUD_PROTOCOL_PREFIX = {
  S3: "s3://",
  GS: "gs://",
} as const;

/**
 * Detects the cloud storage protocol from a path.
 */
export const getCloudProtocol = (path: string): CloudProtocol | null => {
  const p = (path || "").trim().toLowerCase();
  if (p.startsWith(CLOUD_PROTOCOL_PREFIX.S3)) {
    return CLOUD_PROTOCOL.S3;
  }
  if (p.startsWith(CLOUD_PROTOCOL_PREFIX.GS)) {
    return CLOUD_PROTOCOL.GS;
  }
  return null;
};

/**
 * Parses a cloud storage path (Google Cloud Storage or Amazon S3) into its components.
 *
 * @param path - The cloud storage path to parse (gs:// or s3:// format)
 * @returns Object containing provider, bucket, and prefix, or null if parsing fails
 *
 * @example
 * parseCloudPath("gs://my-bucket/folder/file.txt");
 * // Returns: { provider: DataSourceProvider.Google, bucket: "my-bucket", prefix: "folder/file.txt" }
 *
 * parseCloudPath("s3://my-bucket/data/");
 * // Returns: { provider: DataSourceProvider.Amazon, bucket: "my-bucket", prefix: "data/" }
 *
 * parseCloudPath("invalid-path");
 * // Returns: null
 */
export const parseCloudPath = (
  path: string
): { provider: DataSourceProvider; bucket: string; prefix: string } | null => {
  if (path.startsWith(CLOUD_PROTOCOL_PREFIX.GS)) {
    const match = path.match(/^gs:\/\/([^/]+)(?:\/(.*))?$/);
    if (match) {
      return {
        provider: DataSourceProvider.Google,
        bucket: match[1],
        prefix: match[2] || "",
      };
    }
  } else if (path.startsWith(CLOUD_PROTOCOL_PREFIX.S3)) {
    const match = path.match(/^s3:\/\/([^/]+)(?:\/(.*))?$/);
    if (match) {
      return {
        provider: DataSourceProvider.Amazon,
        bucket: match[1],
        prefix: match[2] || "",
      };
    }
  }
  return null;
};

/**
 * Strips the protocol prefix (gs:// or s3://) from a cloud storage path.
 *
 * @param path - The cloud storage path with protocol prefix
 * @returns The path without the protocol prefix
 *
 * @example
 * stripProtocol("gs://bucket/folder/file.txt");  // Returns: "bucket/folder/file.txt"
 * stripProtocol("s3://bucket/data.csv");         // Returns: "bucket/data.csv"
 * stripProtocol("local/path");                   // Returns: "local/path" (unchanged)
 */
export const stripProtocol = (path: string): string => {
  if (path.startsWith(CLOUD_PROTOCOL_PREFIX.GS)) {
    return path.substring(CLOUD_PROTOCOL_PREFIX.GS.length);
  }
  if (path.startsWith(CLOUD_PROTOCOL_PREFIX.S3)) {
    return path.substring(CLOUD_PROTOCOL_PREFIX.S3.length);
  }
  return path;
};

/**
 * Computes the common path prefix to remove from a list of cloud storage paths.
 * Keeps the last common segment for context (e.g., folder name before divergence).
 *
 * @param paths - Array of cloud storage paths to analyze
 * @returns The common prefix string to remove, or empty string if no common prefix
 *
 * @example
 * computeCommonPrefix(["s3://bucket/folder/file1.txt", "s3://bucket/folder/file2.txt"]);
 * // Returns: "bucket/" (removes bucket, keeps folder for context)
 *
 * computeCommonPrefix(["s3://bucket/a/file1.txt", "s3://bucket/b/file2.txt"]);
 * // Returns: "" (only bucket is common, not enough for prefix removal)
 *
 * computeCommonPrefix(["gs://bucket/dir1/dir2/file1.txt", "gs://bucket/dir1/dir2/file2.txt"]);
 * // Returns: "bucket/dir1/" (keeps dir2 for context)
 */
export const computeCommonPrefix = (paths: string[]): string => {
  if (paths.length === 0) return "";

  // Strip protocol prefix (gs:// or s3://)
  const strippedPaths = paths.map(stripProtocol);

  // Split paths into segments
  const splitPaths = strippedPaths.map(p => p.split("/"));

  // Find common segments (excluding the last segment which is filename)
  const commonSegments: string[] = [];
  const minLength = Math.min(...splitPaths.map(p => p.length));

  for (let i = 0; i < minLength - 1; i++) {
    // Exclude last segment (filename)
    const segment = splitPaths[0][i];
    if (splitPaths.every(p => p[i] === segment)) {
      commonSegments.push(segment);
    } else {
      break;
    }
  }

  // Only remove prefix if there are 2+ common segments
  // - 0-1 common segments: return "" (keep full path including bucket)
  // - 2+ common segments: remove all but last, preserving some folder context
  // Example: ["bucket", "folder1", "folder2"] → removes "bucket/folder1/", keeps "folder2/file.txt"
  if (commonSegments.length <= 1) {
    return "";
  }

  return commonSegments.slice(0, -1).join("/") + "/";
};

/**
 * Gets the relative path by stripping the protocol and removing a common prefix.
 *
 * @param fullPath - The full cloud storage path (with gs:// or s3:// prefix)
 * @param commonPrefix - The common prefix to remove (without protocol)
 * @returns The relative path after removing protocol and common prefix
 *
 * @example
 * getRelativePath("s3://bucket/folder/file.txt", "bucket/");
 * // Returns: "folder/file.txt"
 *
 * getRelativePath("gs://bucket/data/file.csv", "");
 * // Returns: "bucket/data/file.csv"
 */
export const getRelativePath = (
  fullPath: string,
  commonPrefix: string
): string => {
  const stripped = stripProtocol(fullPath);
  if (commonPrefix && stripped.startsWith(commonPrefix)) {
    return stripped.substring(commonPrefix.length);
  }
  return stripped;
};
